// src/utils/lottery.js
//
// Shared lottery domain helpers. Pure functions, no React.
// Keeping these centralized makes it easy to swap label/copy without
// hunting through every page.

export const LOTTERY = {
  MEGA_MILLIONS: "MEGA_MILLIONS",
  POWER_BALL: "POWER_BALL",
};

/**
 * Display name for a lottery type.
 */
export const lotteryName = (type) => {
  switch (String(type || "").toUpperCase()) {
    case "MEGA_MILLIONS": return "Mega Millions";
    case "POWER_BALL":    return "Powerball";
    default:              return String(type || "");
  }
};

/**
 * Normalize a backend lottery type, defensive against casing/whitespace.
 */
export const normalizeLotteryType = (type) => {
  const v = String(type || "").toUpperCase().trim();
  if (v === "MEGA_MILLIONS") return LOTTERY.MEGA_MILLIONS;
  if (v === "POWER_BALL")    return LOTTERY.POWER_BALL;
  return v;
};

/**
 * Backend returns the special ball under one of three keys depending on
 * the lottery. This unifies that.
 */
export const getSpecialBall = (line) =>
  line?.extraBall ?? line?.megaBall ?? line?.powerBall ?? null;

/**
 * Format a draw line for the UI as a plain array of numbers, with the
 * special ball at the end. Useful for "Copy to clipboard" + render loops.
 */
export const lineToNumbers = (line) => {
  if (!line) return [];
  return [line.num1, line.num2, line.num3, line.num4, line.num5, getSpecialBall(line)];
};

/**
 * Tally how many of the 5 main numbers + the special ball matched
 * the winning draw.
 *
 * Returns { mainMatched, specialMatched, total } or null if we have no
 * winning numbers attached yet.
 */
export const tallyMatches = (line, winning) => {
  if (!line || !winning) return null;
  const winMain = [winning.num1, winning.num2, winning.num3, winning.num4, winning.num5]
    .filter((v) => v !== null && v !== undefined);
  if (winMain.length === 0) return null;

  const mine = [line.num1, line.num2, line.num3, line.num4, line.num5];
  const mainMatched = mine.filter((n) => winMain.includes(n)).length;
  const winSpecial = getSpecialBall(winning);
  const mineSpecial = getSpecialBall(line);
  const specialMatched =
    winSpecial !== null && mineSpecial !== null && winSpecial === mineSpecial ? 1 : 0;

  return { mainMatched, specialMatched, total: mainMatched + specialMatched };
};

/**
 * Format a US dollar jackpot. Pass a number in dollars or null.
 *   2_400_000_000  -> "$2.4B"
 *   340_000_000    -> "$340M"
 *   null           -> "—"
 */
export const formatJackpot = (dollars) => {
  if (dollars === null || dollars === undefined) return "—";
  const n = Number(dollars);
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (n >= 1_000_000)     return `$${Math.round(n / 1_000_000)}M`;
  if (n >= 1_000)         return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
};

/**
 * Constants — labels rendered in the UI for each lottery's draw schedule.
 * Real draws happen ~11pm ET; if we ever need that precision, switch to
 * a backend-provided "nextDrawAt" instead.
 */
export const LOTTERY_META = {
  MEGA_MILLIONS: {
    label: "Mega Millions",
    schedule: "Tue & Fri",
    weekdays: [2, 5], // Tue, Fri
    specialLabel: "Mega Ball",
    icon: "star",
  },
  POWER_BALL: {
    label: "Powerball",
    schedule: "Mon, Wed & Sat",
    weekdays: [1, 3, 6],
    specialLabel: "Power Ball",
    icon: "bolt",
  },
};

export const GAMES_PER_CREDIT = 5;
